import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { formatUSD, formatDate, cleanOperatorName } from "@/lib/utils";
import { Search, CalendarDays, Download } from "lucide-react";
import StatusPill from "@/components/StatusPill";
import TransactionRowActions from "@/components/TransactionRowActions";

const RANGE_KEYS = ["today","yesterday","7d","30d","this_month","last_month","custom"];

function isoStart(range, customFrom) {
  // Compute the INCLUSIVE start-of-window at local midnight.
  const today = new Date(); today.setHours(0, 0, 0, 0);
  if (range === "today") return today;
  if (range === "yesterday") { const d = new Date(today); d.setDate(d.getDate() - 1); return d; }
  // "Last 7 days" means today + 6 previous days = 7 days total.
  if (range === "7d") { const d = new Date(today); d.setDate(d.getDate() - 6); return d; }
  if (range === "30d") { const d = new Date(today); d.setDate(d.getDate() - 29); return d; }
  if (range === "this_month") { const d = new Date(today.getFullYear(), today.getMonth(), 1); return d; }
  if (range === "last_month") { const d = new Date(today.getFullYear(), today.getMonth() - 1, 1); return d; }
  if (range === "custom" && customFrom) return new Date(customFrom + "T00:00:00");
  return today;
}
function isoEnd(range, customTo) {
  // Compute the EXCLUSIVE end-of-window (start of the day AFTER the last
  // included day). Backend uses `$lt` so this is safe.
  const today = new Date(); today.setHours(0, 0, 0, 0);
  if (range === "yesterday") { return today; }
  if (range === "last_month") { const d = new Date(today.getFullYear(), today.getMonth(), 1); return d; }
  if (range === "custom" && customTo) { const d = new Date(customTo + "T00:00:00"); d.setDate(d.getDate() + 1); return d; }
  // today / 7d / 30d / this_month all end at "tomorrow 00:00"
  const d = new Date(today); d.setDate(d.getDate() + 1); return d;
}

export default function SubagentTransactions() {
  const { t } = useTranslation();
  const [urlParams] = useSearchParams();
  // scope=masters ⇒ show only MASTER_AGENT transactions; default = AGENT.
  const isMastersScope = urlParams.get("scope") === "masters";
  const roleScope = isMastersScope ? "MASTER_AGENT" : "AGENT";
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [agents, setAgents] = useState([]);
  const [range, setRange] = useState("7d");
  const [customFrom, setCustomFrom] = useState(() => new Date(Date.now() - 7 * 86400e3).toISOString().slice(0, 10));
  const [customTo, setCustomTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const rangeLabel = useMemo(() => {
    if (range === "custom") return `${customFrom} → ${customTo}`;
    return t(`ui.range_${range}`);
  }, [range, customFrom, customTo, t]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const start = isoStart(range, customFrom).toISOString();
      const end = isoEnd(range, customTo).toISOString();
      const params = new URLSearchParams({
        subagents_only: "true",
        role_scope: roleScope,
        start,
        end,
        limit: String(pageSize),
        skip: String((page - 1) * pageSize),
      });
      if (debouncedQ) params.set("q", debouncedQ);
      const tx = await api.get(`/topups?${params.toString()}`);
      const data = tx.data;
      const items = Array.isArray(data) ? data : (data.items || []);
      setRows(items);
      setTotal(Array.isArray(data) ? items.length : (data.total ?? items.length));
    } finally { setLoading(false); }
  }, [range, customFrom, customTo, roleScope, pageSize, page, debouncedQ]);

  // Independent one-shot fetch of the agents/master-agents dropdown so the
  // main paginated `load` doesn't refetch when this side-list arrives.
  useEffect(() => {
    let mounted = true;
    const listUrl = isMastersScope ? "/master-agents" : "/agents?limit=500";
    api.get(listUrl).then(({ data: ag }) => {
      if (!mounted) return;
      const ags = Array.isArray(ag) ? ag : (ag.items || []);
      setAgents(ags);
    }).catch(() => {});
    return () => { mounted = false; };
  }, [isMastersScope]);

  // Debounce search → backend
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedQ(q.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => { load(); }, [load]);

  const agentByUsername = useMemo(() => {
    const m = {};
    for (const a of agents) m[a.username] = a;
    return m;
  }, [agents]);

  // Server-side: the rows are already filtered. The frontend just maps them.
  const filtered = rows;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-6" data-testid="subagent-tx-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
          {isMastersScope ? t("role.MASTER_AGENT") : t("role.AGENT")}
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
          {isMastersScope ? t("nav.folder_master_tx") : t("sub_tx.title")}
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          {isMastersScope
            ? t("ui.subagent_scope_masters")
            : t("ui.subagent_scope_agents")}
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
          <div className="relative inline-block">
            <button
              data-testid="subagent-range-btn"
              onClick={() => setRangeOpen((v) => !v)}
              className="inline-flex items-center gap-2 h-10 px-3 rounded-lg border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 text-sm font-semibold"
            >
              <CalendarDays className="w-4 h-4 text-[#0F62FE]" />
              {rangeLabel}
            </button>
            {rangeOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setRangeOpen(false)} />
                <div className="absolute left-0 mt-1 w-64 rounded-lg border border-slate-200 bg-white shadow-lg z-40 py-1 text-sm">
                  {RANGE_KEYS.map((k) => (
                    <button
                      key={k}
                      onClick={() => { setRange(k); if (k !== "custom") setRangeOpen(false); }}
                      className={`w-full text-left px-3 py-2 hover:bg-slate-50 ${range === k ? "text-[#0F62FE] font-semibold bg-blue-50/40" : "text-slate-700"}`}
                    >{t(`ui.range_${k}`)}</button>
                  ))}
                  {range === "custom" && (
                    <div className="p-3 border-t border-slate-200 space-y-2">
                      <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="h-9 w-full px-2 rounded-md border border-slate-300 text-sm" />
                      <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="h-9 w-full px-2 rounded-md border border-slate-300 text-sm" />
                      <button onClick={() => setRangeOpen(false)} className="w-full h-9 rounded-md bg-[#0F62FE] text-white text-xs font-semibold">{t("ui.apply")}</button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="relative w-full sm:flex-1 sm:max-w-xl">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="subagent-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("ui.search_tx")}
              className="h-10 w-full pl-9 pr-3 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F62FE]"
            />
          </div>
          <button
            data-testid="subagent-export-csv"
            onClick={async () => {
              const token = localStorage.getItem("token");
              const start = isoStart(range, customFrom).toISOString();
              const end = isoEnd(range, customTo).toISOString();
              const res = await fetch(
                `${API}/reports/export.csv?kind=topups&start=${encodeURIComponent(start)}&end=${encodeURIComponent(end)}&role_scope=${roleScope}&subagents_only=true`,
                { headers: { Authorization: `Bearer ${token}` } },
              );
              const blob = await res.blob();
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `${isMastersScope ? "master-agent" : "subagent"}-transactions-${range}.csv`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-300 text-slate-700 text-sm font-medium hover:bg-slate-50"
          >
            <Download className="w-4 h-4" /> {t("ui.export_csv")}
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="subagent-tx-table">
            <thead>
              <tr>
                {[
                  { k: "tx_id", label: t("dashboard.col_id") },
                  { k: "agent_id", label: "Agent ID" },
                  { k: "username", label: t("agents.col_username") },
                  { k: "product", label: t("topup.product") },
                  { k: "phone", label: t("topup.phone") },
                  { k: "date", label: t("wallet.col_date") },
                  { k: "amount", label: t("wallet.col_amount") },
                  { k: "status", label: t("common.status") },
                  { k: "commission", label: t("topup.commission") },
                  { k: "actions", label: t("common.actions") },
                ].map(({ k, label }) => (
                  <th key={k} className="bg-slate-50 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider px-4 py-3 border-b border-slate-200 whitespace-nowrap">{label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={10} className="px-4 py-8 text-center text-slate-500">{t("ui.loading_ellipsis")}</td></tr>}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-slate-500">{t("ui.no_subagent_tx")}</td></tr>
              )}
              {filtered.map((t) => {
                const a = agentByUsername[t.agent_username] || {};
                return (
                  <tr key={t.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-mono text-[11px] text-slate-500">{(t.id || "").slice(0, 8).toUpperCase()}</td>
                    <td className="px-4 py-3 font-mono text-[11px] text-slate-700">{a.agent_code || "—"}</td>
                    <td className="px-4 py-3 text-slate-700">{t.agent_username}</td>
                    <td className="px-4 py-3 text-slate-700 text-xs">{cleanOperatorName(t.operator_name)}</td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-700">{t.phone_number}</td>
                    <td className="px-4 py-3 text-slate-700 text-xs whitespace-nowrap">{formatDate(t.created_at)}</td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{formatUSD(t.retail_price_usd)}</td>
                    <td className="px-4 py-3"><StatusPill status={t.status} /></td>
                    <td className="px-4 py-3 text-emerald-700 font-semibold">{formatUSD(t.commission_amount || 0)}</td>
                    <td className="px-4 py-3"><TransactionRowActions topup={t} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between text-xs text-slate-500" data-testid="subagent-pagination">
          <div>
            {t("ui.showing_range", { from: total === 0 ? 0 : (page - 1) * pageSize + 1, to: Math.min(page * pageSize, total), total: total.toLocaleString(), noun: t("ui.transactions_lower") })}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="h-8 px-2 rounded border border-slate-300 bg-white"
              data-testid="subagent-page-size"
            >
              {[25, 50, 100, 200].map((s) => <option key={s} value={s}>{t("ui.per_page", { n: s })}</option>)}
            </select>
            <button
              data-testid="subagent-prev-page"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="h-8 px-3 rounded border border-slate-300 bg-white disabled:opacity-40 font-semibold"
            >{t("ui.prev")}</button>
            <span className="font-mono">{page} / {totalPages}</span>
            <button
              data-testid="subagent-next-page"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className="h-8 px-3 rounded border border-slate-300 bg-white disabled:opacity-40 font-semibold"
            >{t("ui.next")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
